import React from 'react';
import { ChevronDown } from 'lucide-react';
import { motion, useReducedMotion } from 'motion/react';

import {
    BouncyDisclosurePanel,
    CHEVRON_TRANSITION,
    ROW_TRANSITION,
} from '@/components/motion/bouncy-accordion';
import { cn } from '@/lib/utils';

/**
 * Der Rahmen einer Ressourcen-Kachel im Zuschnitt des Bouncy-Akkordeons: eine
 * Kartenfläche mit weit gerundeten Ecken, die den Inhalt beschneidet, damit die
 * federnde Höhe nicht über die Rundung hinausläuft.
 *
 * Als Klasse statt als Bauteil, damit die Kacheln ihren eigenen Wurzelknoten
 * behalten (mehrere von ihnen hängen Verhalten daran). Die Klasse `resource`
 * bleibt darin: An ihr hängen Regeln für fremdes DOM in `App.sass`
 * (`.resource .tableScrollWrapper`, `.resource .title`).
 */
export const RESOURCE_CARD_CLASS =
    'resource my-1.5 box-border w-full overflow-hidden rounded-[28px] bg-muted/60 text-card-foreground';

/**
 * Der Kanal, über den eine Kachel ihrer Gruppe meldet, ob sie offen steht.
 *
 * Die Kacheln verwalten ihren Auf-/Zu-Zustand jede für sich (teils in einer
 * modulweiten Karte, damit er das Neuzeichnen überlebt). Die Gruppe darum muss
 * ihn trotzdem kennen — nur so weiß eine Zeile, ob ihre Nachbarin offen ist und
 * ihre Kante deshalb eckig oder rund läuft.
 */
const ResourceCardReportContext = React.createContext<((open: boolean) => void) | null>(null);

interface ResourceGroupCardProps {
    open: boolean;
    previousOpen: boolean;
    nextOpen: boolean;
    isFirst: boolean;
    isLast: boolean;
    onOpenChange: (open: boolean) => void;
    /** Die Kachel schwebt beim ersten Erscheinen herein. */
    animateIn?: boolean;
    /** Verzögerung des Auftritts in Sekunden. */
    enterDelay?: number;
    children: React.ReactNode;
}

/**
 * Eine Kachel als Zeile einer zusammenhängenden Gruppe — der Zuschnitt des
 * Bouncy-Akkordeons.
 *
 * Geschlossene Zeilen liegen bündig aneinander: Nur die Außenkanten der Gruppe
 * sind gerundet, die Innenkanten laufen eckig. Eine geöffnete Zeile löst sich
 * heraus — sie rundet sich rundum und schiebt sich mit Abstand von ihren
 * Nachbarinnen weg. Radien und Abstand federn, statt zu springen.
 */
export const ResourceGroupCard = ({
    open,
    previousOpen,
    nextOpen,
    isFirst,
    isLast,
    onOpenChange,
    animateIn = false,
    enterDelay = 0,
    children,
}: ResourceGroupCardProps) => {
    const reduce = useReducedMotion();
    const startsGroup = open || isFirst || previousOpen;
    const endsGroup = open || isLast || nextOpen;
    const separatedFromPrevious = !isFirst && (open || previousOpen);

    return (
        <motion.div
            layout='position'
            initial={animateIn && !reduce ? { opacity: 0, y: 12 } : false}
            animate={{
                opacity: 1,
                y: 0,
                marginTop: separatedFromPrevious ? 12 : 0,
            }}
            transition={
                reduce
                    ? { duration: 0 }
                    : {
                          opacity: { duration: 0.32, delay: enterDelay },
                          y: { duration: 0.32, delay: enterDelay },
                          marginTop: ROW_TRANSITION,
                          layout: ROW_TRANSITION,
                      }
            }
        >
            <motion.div
                data-state={open ? 'open' : 'closed'}
                initial={false}
                animate={{
                    borderTopLeftRadius: startsGroup ? 28 : 0,
                    borderTopRightRadius: startsGroup ? 28 : 0,
                    borderBottomLeftRadius: endsGroup ? 28 : 0,
                    borderBottomRightRadius: endsGroup ? 28 : 0,
                }}
                transition={reduce ? { duration: 0 } : ROW_TRANSITION}
                // Die Fläche liegt dauerhaft als leichtes Grau hinter der Zeile,
                // nicht erst beim Überfahren.
                className='resource box-border w-full overflow-hidden bg-muted/60 text-card-foreground'
            >
                <ResourceCardReportContext.Provider value={onOpenChange}>
                    {children}
                </ResourceCardReportContext.Provider>
            </motion.div>
        </motion.div>
    );
};

/**
 * Kopfzeile einer Ressourcen-Kachel: **die ganze Zeile** klappt auf, nicht nur
 * das Chevron. Zeigefinger und Tastatur treffen dasselbe Ziel, und der Kopf
 * meldet über `aria-expanded`, in welchem Zustand er ist.
 *
 * Aktions-Knöpfe (Download, Vergrößern, „Zum Gebäude") liegen als `actions`
 * **neben** dem Kopf-Button, nicht darin: ein Button in einem Button ist
 * ungültiges HTML und schluckt in der Praxis den inneren Klick.
 *
 * Ein Chevron, das sich dreht — kein Icon-Tausch: nur so lässt sich der
 * Zustandswechsel überhaupt animieren. Es dreht mit derselben federnden Physik,
 * mit der der Inhalt darunter aufgeht (`bouncy-accordion`).
 */
interface ResourceDisclosureHeaderProps {
    expanded: boolean;
    onToggle: (event: React.MouseEvent<HTMLButtonElement>) => void;
    /** Wandert vom alten Chevron-Knopf hierher — Playwright greift darauf zu. */
    testId: string;
    icon?: React.ReactNode;
    title: React.ReactNode;
    /** Eigene Knöpfe rechts, außerhalb des Kopf-Buttons. */
    actions?: React.ReactNode;
    className?: string;
    label?: string;
}

export const ResourceDisclosureHeader = ({
    expanded,
    onToggle,
    testId,
    icon,
    title,
    actions,
    className,
    label,
}: ResourceDisclosureHeaderProps) => {
    const reduce = useReducedMotion();
    const report = React.useContext(ResourceCardReportContext);

    React.useEffect(() => {
        report?.(expanded);
    }, [expanded, report]);

    return (
        <div className={cn('flex w-full items-center', className)}>
            <button
                type='button'
                data-testid={testId}
                aria-expanded={expanded}
                aria-label={label}
                onClick={onToggle}
                className='flex min-h-[54px] min-w-0 flex-1 items-center gap-4 px-5 text-left outline-none transition-colors hover:bg-foreground/[0.04] focus-visible:bg-foreground/[0.04] motion-reduce:transition-none'
            >
                {icon}
                <span className='title min-w-0 flex-1 text-[15px] font-medium text-foreground'>{title}</span>
                {/* Das Chevron steht rechts und dreht sich federnd — wie im
                    Bouncy-Akkordeon, aus dem die Bewegung stammt. */}
                <motion.span
                    aria-hidden='true'
                    className='grid h-6 w-6 shrink-0 place-items-center text-muted-foreground'
                    animate={{ rotate: expanded ? 180 : 0 }}
                    transition={reduce ? { duration: 0 } : CHEVRON_TRANSITION}
                >
                    <ChevronDown className='h-4 w-4' />
                </motion.span>
            </button>
            {actions ? <div className='flex shrink-0 items-center gap-1 pr-4'>{actions}</div> : null}
        </div>
    );
};

/**
 * Der aufklappbare Teil einer Kachel. Die Höhe federt zwischen 0 und der
 * gemessenen Inhaltshöhe (`BouncyDisclosurePanel`) — gemessen statt über
 * `grid-template-rows` gerechnet, weil eine Feder eine Zahl braucht, über die
 * sie hinausschwingen kann. Ein `ResizeObserver` hält die Zahl aktuell, während
 * der Inhalt noch wächst.
 *
 * Der Inhalt wird **erst beim ersten Öffnen** gebaut und bleibt danach hängen:
 * ungeöffnete Kacheln kosten so nichts (kein Bild wird geladen, kein Diagramm
 * gezeichnet), und das Zuklappen kann trotzdem animieren.
 *
 * Nicht geeignet für Karten und 3D-Ansichten: die messen beim Aufbau ihren
 * Container und stünden in der zusammengeklappten Zeile auf 0 Pixel Höhe. Diese
 * Kacheln behalten ihr bedingtes Rendern.
 */
export const ResourceDisclosurePanel = ({
    expanded,
    children,
    className,
}: {
    expanded: boolean;
    children: React.ReactNode;
    className?: string;
}) => {
    const [everOpened, setEverOpened] = React.useState(expanded);

    React.useEffect(() => {
        if (expanded) setEverOpened(true);
    }, [expanded]);

    const reduce = useReducedMotion();

    return (
        <BouncyDisclosurePanel
            open={expanded}
            reduce={reduce}
            className={className}
            innerClassName='px-5 pb-5'
        >
            {everOpened ? children : null}
        </BouncyDisclosurePanel>
    );
};
